<template>
	<DataTable
		:value="locatorSettings"
		:loading="loading"
		class="p-datatable-sm"
		:paginator="true"
		paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
		:rowsPerPageOptions="[25,50,100]"
		:currentPageReportTemplate="'{first} ' + $t('Squeeze.General.To') + ' {last} ' + $t('Squeeze.General.Of') + ' {totalRecords}  '"
		:autoLayout="true"
		columnResizeMode="fit"
		@row-dblclick="openEditDialog($event.data)"
		:scrollable="true"
		scrollHeight="flex"
		:rowHover="true"
		:rows="tableListPagination.pageSize"
		@page="onChangePage($event)"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.Locators.LocatorsAttributes') }) }}
		</template>
		<Column field="setting" :header="$t('Squeeze.Locators.Name')" :sortable="false" filterMatchMode="contains" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getSettingsDescription(slotProps.data.setting)">
					{{ getSettingsDescription(slotProps.data.setting) }}
				</div>
			</template>
		</Column>
		<Column field="value" :header="$t('Squeeze.Locators.Value')" :sortable="false" filterMatchMode="contains" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.value">
					{{ slotProps.data.value }}
				</div>
			</template>
		</Column>
		<!-- Delete & Edit -->
		<Column style="min-width: 7rem; max-width: 7rem; text-align: right;">
			<template #body="slotProps">
				<Button v-tooltip.bottom="$t('Squeeze.Locators.Edit')" icon="mdi mdi-pencil-outline" class="p-button-lg p-button-rounded p-button-success p-button-text" @click="openEditDialog(slotProps.data)" />
				<Button v-tooltip.bottom="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button-lg p-button-rounded p-button-danger p-button-text" @click="openDeleteDialog(slotProps.data)" />
			</template>
		</Column>

		<template #paginatorstart>
			<Button :label="$t('Squeeze.General.NewEntry')" type="button" icon="mdi mdi-plus" class="p-button" @click="openEditDialog({id: 0, locatorId: undefined, sortOrder: undefined, setting: '', value: ''})" />
		</template>
		<template #paginatorend></template>
	</DataTable>
</template>

<script lang="ts">
import {defineComponent, onMounted, PropType, reactive} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {LocatorSetting} from "@dex/squeeze-client-ts";
import Tooltip from "primevue/tooltip";
import useSqueezeStore from "@/apps/squeeze/store";
import {ClientManager} from "@/singletons/ClientManager";
import {TableListSettings, TableSettings} from "@/util/TableSettings";
import {useRoute} from "vue-router";

export default defineComponent({
	name: 'LocatorAttributes',
	components: {
		DataTable,
		Column,
	},
	props: {
		/** Show loading in the table? */
		loading: {
			type: Boolean,
			default: false,
		},
		/** Currently active document-class */
		locatorId: {
			type: Number,
			default: 0,
		},
		/** All available Locators */
		locatorSettings: {
			type: Array as PropType<LocatorSetting[]>,
			default: () => [],
		},
		/** All Valid Settings */
		supportedLocatorSettings: {
			type: Array as PropType<LocatorSetting[]>,
			default: () => [],
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: ["openEntryDialog", "openDelete"],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const route = useRoute();

		/** Vuex Store */
		const store = useSqueezeStore();

		/** User API endpoint */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Current table list pagination */
		const tableListPagination = reactive<TableListSettings>({});

		/** Data of selected row */
		const rowData = reactive<any>({});

		/**
		 * Opens the Edit-Dialog
		 * @param {LocatorSetting} data Data to edit
		 */
		const openEditDialog = (data: LocatorSetting) => {
			Object.assign(rowData, data);
			emit("openEntryDialog", rowData, props.locatorId);
		}

		/**
		 * Opens the Delete Dialog
		 * @param {LocatorSetting} data Data from the Document-Class that should be deleted
		 */
		const openDeleteDialog = (data: LocatorSetting) => {
			Object.assign(rowData, data);
			emit("openDelete", rowData);
		}

		/**
		 * Get description of setting
		 * @param {string} settingName
		 */
		const getSettingsDescription = (settingName: string) => {
			if (!props.supportedLocatorSettings.length) {
				return settingName;
			}

			const locatorType = props.supportedLocatorSettings.find((locatorSetting) => {
				return locatorSetting.setting === settingName;
			});

			if (!locatorType) {
				return settingName;
			}

			return locatorType.setting;
		}

		/**
		 * Triggered when page is changed
		 * @param event
		 */
		const onChangePage = (event: { page: number; rows: number }) => {
			tableListPagination.pageSize = event.rows;
			TableSettings.saveTableListPagination(t, toast, store, userApi, route.name, event.rows);
		}

		onMounted(() =>{
			tableListPagination.pageSize = TableSettings.getTableListPagination(store, route.name);
		})

		return {
			t,
			toast,
			route,
			store,
			tableListPagination,
			rowData,
			openEditDialog,
			openDeleteDialog,
			getSettingsDescription,
			onChangePage,
		};
	},
});

</script>

<style scoped>

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>
